import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function Landing() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 stagger-in">
            <div className="font-mono-label text-primary">Porcelain · Est. 2026</div>
            <h1 className="font-serif-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tighter text-foreground">
              The quiet art of <em className="italic">sculpting</em> teeth — taught by hand, one ridge at a time.
            </h1>
            <p className="mt-8 max-w-xl text-base text-muted-foreground leading-relaxed">
              A masterclass in additive wax-up, occlusal morphology, and anatomical precision. Built for dental students and practicing clinicians who want the craftsman's eye.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <Link to="/courses" className="btn-oxblood text-sm inline-flex items-center gap-2" data-testid="hero-browse-courses">
                Browse the curriculum <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/anatomy" className="btn-outline-mono text-sm" data-testid="hero-anatomy-cta">
                Open anatomy library
              </Link>
            </div>
            <div className="mt-12 pt-8 editorial-rule grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "04", v: "Course tracks" },
                { k: "18+", v: "Hours of content" },
                { k: "32", v: "FDI teeth covered" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-serif-display text-3xl">{s.k}</div>
                  <div className="font-mono-label text-muted-foreground mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=1200&q=80"
                alt="Dental sculpting close-up"
                className="w-full h-[500px] object-cover"
                style={{ borderRadius: 2 }}
              />
              <div className="absolute -bottom-4 -left-4 bg-background border border-border px-4 py-3" style={{ borderRadius: 2 }}>
                <div className="font-mono-label text-primary">Lesson 02 · 26 min</div>
                <div className="font-serif-display text-xl mt-1">Additive Cone Technique</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mono strip */}
      <section className="border-b border-border bg-secondary/50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-8 overflow-x-auto font-mono-label text-muted-foreground whitespace-nowrap">
          <span>FDI 11 · Central Incisor</span><span>—</span>
          <span>FDI 13 · Canine</span><span>—</span>
          <span>FDI 14 · Premolar</span><span>—</span>
          <span>FDI 16 · Maxillary Molar</span><span>—</span>
          <span>FDI 36 · Mandibular Molar</span><span>—</span>
          <span>Canine Guidance</span><span>—</span>
          <span>Group Function</span><span>—</span>
          <span>Y-Groove</span>
        </div>
      </section>

      {/* Featured courses */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="font-mono-label text-primary">The Curriculum</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light mt-3 tracking-tight">Four tracks. One craftsman's language.</h2>
          </div>
          <Link to="/courses" className="hidden sm:flex items-center gap-2 text-sm hover:text-primary transition-colors" data-testid="courses-see-all">
            See all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
          {courses.slice(0, 4).map((c, i) => (
            <Link
              key={c.id}
              to={`/courses/${c.id}`}
              data-testid={`course-card-${c.slug}`}
              className={`porcelain-card p-6 flex flex-col justify-between group ${i === 0 ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"}`}
              style={{ minHeight: i === 0 ? 480 : 230 }}
            >
              <div className="flex items-start justify-between">
                <div className="font-mono-label text-primary">{c.category} · {c.level}</div>
                <div className="flex items-center gap-2">
                  {c.is_premium && <span className="font-mono-label bg-foreground text-background px-1.5 py-0.5" style={{ borderRadius: 2 }}>PRO</span>}
                  <div className="font-mono-label text-muted-foreground">{c.lesson_count} LESSONS</div>
                </div>
              </div>
              {i === 0 && (
                <img src={c.cover_image} alt={c.title} className="my-6 w-full h-56 object-cover" style={{ borderRadius: 2 }} />
              )}
              <div>
                <h3 className={`font-serif-display tracking-tight ${i === 0 ? "text-4xl" : "text-2xl"} leading-none`}>
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground max-w-md">{c.subtitle}</p>
                <div className="mt-6 flex items-center gap-2 text-sm group-hover:text-primary transition-colors">
                  Enter course <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Philosophy */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-label text-primary">Philosophy</div>
          </div>
          <div className="md:col-span-8">
            <p className="font-serif-display text-3xl md:text-4xl leading-snug tracking-tight text-foreground">
              "A tooth is a map of forces. We do not invent anatomy — we read it, then sculpt it back. Slowly, patiently, correctly."
            </p>
            <div className="mt-8 font-mono-label text-muted-foreground">— Porcelain Academy</div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-serif-display text-5xl tracking-tight leading-none">Pick up the instrument.</h2>
          <p className="mt-6 text-muted-foreground max-w-md">
            Create a free account to track progress across every course, run quizzes, and ask Porcelain, our AI tutor, anything.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/signup" className="btn-oxblood text-sm" data-testid="cta-signup">Create free account</Link>
            <Link to="/anatomy" className="btn-outline-mono text-sm" data-testid="cta-anatomy">Explore anatomy</Link>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { k: "Hand-sculpted", v: "Every lesson is taught by working technicians." },
            { k: "FDI-indexed", v: "Anatomy library uses clinical numbering." },
            { k: "AI Tutor", v: "Ask Claude 4.5 about any morphology question." },
            { k: "Progress", v: "Quizzes and completion tracking built in." },
          ].map((b) => (
            <div key={b.k} className="porcelain-card p-5">
              <div className="font-mono-label text-primary">{b.k}</div>
              <div className="mt-3 text-sm text-muted-foreground leading-relaxed">{b.v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
